<template>
  <section>
    <Paragraph class="font-jost-regular text-blue-dark text-[25px] leading-[30px] mt-10" text="Verification" />
    <div class="t-results text-lg mt-5">
      <section class="t-results-body grid grid-cols-2 gap-[90px] bg-white">
        <div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Block
            </div>
            <router-link v-if="transactionData && transactionData.uat && transactionData.uat.uatData"
              class="col block px-2.5 flex relative text-[#5B6BD5] cursor-pointer"
              :to="{ name: 'block', params: { chainNumber: transactionData.uat.uatData.chainNumber || $route.params.chainNumber || $route.params.chain, blockNumber: transactionData.uat.uatData.blockNumber } }">
              #{{ transactionData.uat.uatData.blockNumber }}
            </router-link>
          </div>

          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Transaction Fee
            </div>
            <div class="col font-jost-regular px-2.5 text-blue-dark">
              {{ transactionFee }} <span v-if="transactionFee !== 'N/A'" class="uppercase">GEEQ</span>
            </div>
          </div>

          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Timestamp
            </div>
            <div class="col font-jost-regular px-2.5 text-blue-dark">
              {{ getTxCreationDate() }}
            </div>
          </div>

          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              From
            </div>
            <div class="col flex px-2.5 gap-[10px]">
              <router-link v-if="sender" class="block text-ellipsis text-[16px]"
                :to="{ name: 'account', params: { chainNumber: transactionData.uat.uatData.chainNumber || $route.params.chainNumber || $route.params.chain, address: sender } }">
                <span class="text-[#5B6BD5]">
                  {{ sender }}
                </span>
              </router-link>
              <CopyIcon :isActive="checkIfIsEqual(sender, 'sender')" @click="copy(sender, 'sender')" title="Copy hash"
                class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top" />
            </div>
          </div>

          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              To
            </div>
            <div class="col flex px-2.5 gap-[10px]">
              <router-link v-if="receiver" class="block text-ellipsis text-[16px]"
                :to="{ name: 'account', params: { chainNumber: transactionData.uat.uatData.chainNumber || $route.params.chainNumber || $route.params.chain, address: receiver } }">
                <span class="text-[#5B6BD5]">
                  {{ receiver }}
                </span>
              </router-link>
              <CopyIcon :isActive="checkIfIsEqual(receiver, 'receiver')" @click="copy(receiver, 'receiver')"
                title="Copy hash" class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top" />
            </div>
          </div>

          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Counterparty ID
            </div>
            <div class="col flex px-2.5 gap-[10px]">
              <router-link v-if="counterpartyId && transactionRefHash"
                class="block relative text-ellipsis text-[#5B6BD5] cursor-pointer"
                :to="{ name: 'counterparty', params: { chainNumber: transactionData.uat.uatData.chainNumber || $route.params.chainNumber || $route.params.chain, counterpartyId: counterpartyId }, query: { hash: transactionRefHash } }">
                {{ counterpartyId }}
              </router-link>
              <CopyIcon :isActive="checkIfIsEqual(counterpartyId)" @click="copy(counterpartyId)" title="Copy hash"
                class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top" />
            </div>
          </div>

          <div
            class="first__col__last__row row text-left items-center text-blue-dark px-2.5 py-3.5 result-row flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Subgroup Hash
            </div>
            <div class="col flex px-2.5 text-blue-dark gap-[10px]">
              <span class="block relative text-ellipsis">
                {{ subgroupHash }}
              </span>
              <CopyIcon :isActive="checkIfIsEqual(subgroupHash)" @click="copy(subgroupHash)" title="Copy hash"
                class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top" />
            </div>
          </div>
        </div>

        <div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Chain Number
            </div>
            <div class="col">
              {{ transactionData.uat.uatData.chainNumber }}
            </div>
          </div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Status
            </div>
            <div class="flex items-center gap-3 col font-jost-regular px-2.5">
              <span class="px-2 py-1 text-base font-medium rounded uppercase" 
                  :class="transactionData.status === 'valid' ? 'bg-green-100 text-green-800' : 
                    transactionData.status === 'invalid' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'">
                {{ transactionData.status || 'PENDING' }}
              </span>
            </div>
          </div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Signature
            </div>
            <div class="col flex items-center font-jost-regular px-2.5">
              <p v-if="transactionData" class="block text-ellipsis text-[16px]">
                <span class="text-blue-dark">
                  {{ transactionData.signature }}
                </span>
              </p>
              <CopyIcon :isActive="checkIfIsEqual(transactionData.signature, 'signature')" @click="copy(transactionData.signature, 'signature')"
                title="Copy hash" class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top" />
            </div>
          </div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Position on Stack
            </div>
            <div class="col font-jost-regular px-2.5 text-blue-dark">
              {{ stackPosition }}
            </div>
          </div>
          <div class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Appended Metadata
            </div>
            <div class="col block  px-2.5 text-ellipsis text-blue-dark">
              <span v-if="getCreatorName(transactionData.payload.metadataList) "
                class="block font-jost-regular text-ellipsis">
                {{ getCreatorName(transactionData.payload.metadataList) }}
              </span>
              <span class="block font-jost-regular text-ellipsis">{{ getNftTitle(transactionData.payload.metadataList)
                }}</span>
              <a v-if="getNftFileUrl(transactionData.payload.metadataList) "
                class="block font-jost-regular text-ellipsis text-blue-light" target="_blank" rel="noopener noreferrer"
                :href="getNftFileUrl(transactionData.payload.metadataList)">
                Image URL
              </a>
            </div>
            <!-- <CopyIcon :isActive="checkIfIsEqual(metadataString)" @click="copy(metadataString)" title="Copy hash"
              class="h-[19px] inline-block cursor-pointer align-top" /> -->
          </div>
        </div>
      </section>
    </div>
  </section>

</template>

<script>
import moment from 'moment';
import string from '@/helpers/string';

import CopyIcon from "@/components/svg/CopyIcon.vue";
import Paragraph from "@/components/web/ParagraphComponent";

export default {
  name: 'CounterpartyOverview',
  components: {
    CopyIcon,
    Paragraph
  },
  props: {
    transactionData: {
      type: Object,
      required: true
    },
    transactionRefHash: {
      type: String,
      required: true
    },
  },
  computed: {
    transactionFee() {
      const fee = this.transactionData?.fee
        ?? this.transactionData?.uat?.overview?.fee
        ?? this.transactionData?.uat?.uatData?.fee;

      return fee === null || fee === undefined || fee === '' ? 'N/A' : fee;
    },
    stackPosition() {
      return this.transactionData.uat.overview.stackPosition;
    },
    sender() {
      const sender = this.transactionData.uat.overview.sender;
      return sender || '';
    },
    receiver() {
      const receiver = this.transactionData.uat.overview.receiver;
      return receiver || '';
    },
    counterpartyId() {
      const counterpartyId = this.transactionData.uat.overview.stackId;
      return counterpartyId || '';
    },
    subgroupHash() {
      const subgroupHash = this.transactionData.uat.overview.subgroupHash;
      return subgroupHash || '';
    },
    metadataString() {
      if (!this.modifiedTxPayload) return '';
      const metaList = this.modifiedTxPayload.metadataList;

      return metaList.join(', ');
    },
    modifiedTxPayload() {
      let payload = this.transactionData ? { ...this.transactionData.payload } : {};

      if (!payload.metadataList) return [];

      const modifiedPayload = { metadataList: payload.metadataList.map(item => item.substring(2)) }

      return modifiedPayload;
    },
  },
  data() {
    return {
      copied: '',
    };
  },
  methods: {
    copy(hash, uId = 0) {
      string.copyToClipboard(hash);
      this.setCopied(hash + uId);
    },
    setCopied(hash) {
      this.copied = hash;
    },
    checkIfIsEqual(str, uId = 0) {
      return string.isTextEqualTo(str, this.copied, uId);
    },
    getTxCreationDate() {
      const createdAt = this.transactionData.uat.overview.createdAt;
      if (!createdAt) {
        return '';
      }
      return moment(createdAt).format('YYYY-MM-DD HH:mm:ss');
    },
    getNftTitle(metadata) {
      const title = this.findItemStartingWith(metadata, 't:');
      return title ? title : '';
    },
    getCreatorName(metadata) {
      const artistName = this.findItemStartingWith(metadata, 'u:');
      return artistName ? artistName : '';
    },
    getNftFileUrl(metadata) {
      const fileUrl = this.findItemStartingWith(metadata, 'i:');
      return fileUrl ? fileUrl : '';
    },
    findItemStartingWith(arr, keyName) {
      if (!arr || !arr.length) return null;

      for (const item of arr) {
        if (item.startsWith(keyName)) {
          return item.substring(2);
        }
      }
      return null;
    },
  }
}
</script>

<style>
.transaction-explorer .vjs-value {
  color: #2699FB !important;
  font-size: 18px;
  line-height: 26px;
  font-family: 'Jost-Regular', monospace !important;
}

.transaction-explorer .vjs-tree {
  font-size: 18px;
  line-height: 26px;
  overflow-x: auto !important;
  white-space: nowrap;
  color: #2699FB !important;
}

.transaction-explorer .vjs-key {
  font-family: 'Jost-Regular', monospace !important;
  color: #2699FB !important;
}
</style>
<style scoped>
.col {
  flex: 1;
  /* distributes space on the line equally among items */
  width: calc(100% / 2);
}

.t-results-body {
  padding: 28px 21px;
  border-radius: 8px;
}

.t-results-body .row:first-child {
  border-top: unset;
}

@media (max-width: 992px) {
  .t-results-body {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .first__col__last__row {
    border-bottom: 1px solid #2699FB;
  }
}
</style>
